/**
 * TherapistThreadChat Component
 *
 * Main chat area for a therapist thread. Renders message history as bubbles,
 * supports streaming AI responses with animated cursor, tool use indicators,
 * optimistic pending user messages, and a textarea input with auto-resize.
 * Uses ChatMarkdown (GitHub-flavoured markdown, including tables) for
 * assistant message formatting, and hangs copy / save-as-PDF controls off each
 * completed assistant message.
 */

import { useRef, useEffect, useState, useCallback, KeyboardEvent } from "react";
import { shouldSendOnEnter } from "../../lib/composerKeys";
import {
  Send,
  Loader2,
  MessageSquare,
  AlertCircle,
  Trash2,
  RotateCcw,
  X,
  ArrowDown,
  Square,
  Menu,
} from "lucide-react";
import { useStickyScroll } from "../../hooks/useStickyScroll";
import { ChatMarkdown } from "../chat/ChatMarkdown";
import { MessageActions } from "../chat/MessageActions";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  useTherapistThread,
  useTherapistMessageStreaming,
  useDeleteTherapistThread,
} from "../../hooks/useTherapistChat";
import { ToolUseIndicator } from "./ToolUseIndicator";

/**
 * A completed assistant message plus its copy / save-as-PDF controls.
 *
 * Split out of the message list so each message owns the ref that the PDF
 * export prints from — the export copies this node's rendered HTML, so the
 * saved file matches the screen exactly, tables included.
 */
function AssistantMessage({
  content,
  documentTitle,
}: {
  content: string;
  documentTitle: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted text-foreground">
      <div
        ref={contentRef}
        className="prose prose-sm dark:prose-invert max-w-none"
      >
        <ChatMarkdown>{content}</ChatMarkdown>
      </div>
      <MessageActions
        content={content}
        contentRef={contentRef}
        documentTitle={documentTitle}
      />
    </div>
  );
}

interface TherapistThreadChatProps {
  threadId: string | null;
  onThreadDeleted?: () => void;
  /**
   * Mobile only. When provided, a menu button is rendered in this header
   * instead of the caller stacking a separate "Conversations" row above the
   * chat — one row of chrome rather than two.
   */
  onOpenThreadList?: () => void;
}

export function TherapistThreadChat({
  threadId,
  onThreadDeleted,
  onOpenThreadList,
}: TherapistThreadChatProps) {
  const { data: thread, isLoading } = useTherapistThread(threadId);
  const { streamingState, sendMessage, cancelStreaming, clearError } =
    useTherapistMessageStreaming(threadId);
  const deleteThread = useDeleteTherapistThread();
  const [inputValue, setInputValue] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    isStreaming,
    content,
    error,
    pendingUserMessage,
    activeToolName,
    failedMessage,
  } = streamingState;

  // Follow the stream only while the user is already at the bottom.
  //
  // This previously called scrollIntoView({behavior: "smooth"}) on every token.
  // `content` changes per SSE chunk, so each token started a new smooth-scroll
  // animation that interrupted the one still running — dozens a second, which
  // reads as stutter. It also made reading back through the conversation
  // impossible: every token yanked you to the bottom.
  const { containerRef, isPinnedToBottom, scrollToBottom, handleScroll } =
    useStickyScroll([thread?.messages, content, pendingUserMessage]);

  // Auto-resize textarea
  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [inputValue, resizeTextarea]);

  // Cleanup streaming on unmount
  useEffect(() => {
    return () => {
      cancelStreaming();
    };
  }, [cancelStreaming]);

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming) return;
    setInputValue("");
    await sendMessage(trimmed);
  }, [inputValue, isStreaming, sendMessage]);

  const handleRetry = useCallback(async () => {
    if (!failedMessage || isStreaming) return;
    await sendMessage(failedMessage);
  }, [failedMessage, isStreaming, sendMessage]);

  const handleDeleteThread = useCallback(async () => {
    if (!threadId) return;
    try {
      await deleteThread.mutateAsync(threadId);
      setShowDeleteConfirm(false);
      onThreadDeleted?.();
    } catch {
      // Error handled by mutation state
    }
  }, [threadId, deleteThread, onThreadDeleted]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (shouldSendOnEnter(e)) {
      e.preventDefault();
      handleSend();
    }
  };

  // Empty state — no thread selected
  if (!threadId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 px-6 text-center text-muted-foreground">
        <MessageSquare className="mb-3 h-10 w-10" />
        <p className="text-base font-medium">No conversation selected</p>
        <p className="text-sm">Select a conversation or start a new one</p>
        {onOpenThreadList && (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenThreadList}
            className="mt-3 md:hidden"
          >
            <Menu className="mr-2 h-4 w-4" />
            Conversations
          </Button>
        )}
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const messages = thread?.messages ?? [];
  const threadTitle = thread?.title || "New conversation";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-3 py-2 md:px-4">
        {onOpenThreadList && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenThreadList}
            className="shrink-0 px-2 md:hidden"
            aria-label="Open conversations"
            data-testid="open-thread-list-button"
          >
            <Menu className="h-4 w-4" />
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground truncate">
            {threadTitle}
          </p>
          {thread?.partner_name && (
            <p className="text-xs text-muted-foreground truncate">
              About {thread.partner_name}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowDeleteConfirm(true)}
          className="text-muted-foreground hover:text-destructive shrink-0"
          title="Delete conversation"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/*
        Messages area.

        The scroll container is the flex item itself, not a child of one. A
        flex item whose overflow is not `visible` gets an automatic minimum
        size of 0, so it shrinks and scrolls correctly. Nesting it inside a
        wrapper and giving the inner element `h-full` broke that — the height
        resolved against a wrapper that could size to its content, leaving
        nothing to scroll. The wrapper existed only to anchor the jump button,
        which now anchors to the card root instead.
      */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
      >
        {/* Constrained to a readable measure — the dashboard chat column can
              get wide, and full-bleed prose is hard to read. */}
        <div className="mx-auto w-full max-w-3xl space-y-4">
          {messages.length === 0 && !pendingUserMessage && (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Send a message to start the conversation
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {msg.role === "assistant" ? (
                <AssistantMessage
                  content={msg.content}
                  documentTitle={`AttuneWell — ${threadTitle}`}
                />
              ) : (
                <div className="max-w-[80%] rounded-lg px-4 py-2 bg-primary text-primary-foreground">
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              )}
            </div>
          ))}

          {/* Optimistic pending user message */}
          {pendingUserMessage && (
            <div className="flex justify-end">
              <div className="max-w-[80%] rounded-lg px-4 py-2 bg-primary text-primary-foreground">
                <p className="text-sm whitespace-pre-wrap">
                  {pendingUserMessage}
                </p>
              </div>
            </div>
          )}

          {/* Streaming assistant content */}
          {isStreaming && content && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted text-foreground">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ChatMarkdown>{content}</ChatMarkdown>
                  <span className="inline-block w-1.5 h-4 bg-foreground/60 animate-pulse ml-0.5 align-text-bottom" />
                </div>
              </div>
            </div>
          )}

          {/* Status indicator — shows "Thinking..." or active tool.
            Only visible when streaming and no content yet, or when tool is active. */}
          {isStreaming && (!content || activeToolName) && (
            <ToolUseIndicator
              toolName={activeToolName}
              isStreaming={isStreaming}
            />
          )}

          {/* Error display with retry and dismiss */}
          {error && !isStreaming && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-destructive">
                    Something went wrong
                  </p>
                  <p className="text-xs text-destructive/80 mt-0.5 break-words">
                    {error}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearError}
                  className="h-6 w-6 p-0 shrink-0 text-destructive/70 hover:text-destructive"
                  title="Dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              {failedMessage && (
                <div className="flex items-center gap-2 pl-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    className="h-7 text-xs"
                  >
                    <RotateCcw className="h-3 w-3 mr-1.5" />
                    Retry
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/*
        Only shown once the user has scrolled away. Without it, scrolling up
        during a stream is a trap: auto-scroll correctly stops following, and
        there is no way back to the live edge except manual scrolling.
      */}
      {!isPinnedToBottom && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => scrollToBottom()}
          /*
              Needs an opaque fill and a z-index: this floats over message
              text, and `secondary` in dark mode is nearly the same colour as a
              message bubble, so the label read as loose text sitting on top of
              the conversation rather than as a control.
            */
          className="absolute bottom-24 left-1/2 z-10 -translate-x-1/2 gap-1.5 rounded-full border-border bg-background text-foreground shadow-lg hover:bg-accent"
          data-testid="scroll-to-latest-button"
        >
          <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
          Jump to latest
        </Button>
      )}

      {/* Input area */}
      <div className="border-t p-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isStreaming ? "Type your next message..." : "Type a message..."
            }
            rows={1}
            className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            style={{ minHeight: "38px", maxHeight: "150px" }}
          />
          {/*
            While streaming this becomes Stop. cancelStreaming already existed
            on the hook but was wired to nothing but unmount, so a long or
            wrong answer could not be interrupted.
          */}
          {isStreaming ? (
            <Button
              size="sm"
              variant="outline"
              onClick={cancelStreaming}
              className="shrink-0"
              aria-label="Stop generating"
              title="Stop generating"
              data-testid="stop-streaming-button"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleSend}
              disabled={!inputValue.trim()}
              className="shrink-0"
              aria-label="Send message"
              data-testid="send-message-button"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this conversation and all its
              messages. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteThread.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteThread}
              disabled={deleteThread.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteThread.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
